import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { money, fmtDate, normalizeStatus, STATUS_LABELS } from "@/lib/format";
import { toast } from "sonner";
import { Plus, ArrowLeft, PencilSimple, Receipt, ClipboardText, Signature, ChatCircleText, Package } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get(`/customers/${id}`).then((r) => { setData(r.data); setForm(r.data.customer); });
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-slate-400">Loading…</div>;
  const { customer, work_orders, invoices } = data;

  const save = async () => {
    await api.put(`/customers/${id}`, form);
    toast.success("Customer updated");
    setEditOpen(false); load();
  };

  const startInvoice = () => navigate(`/invoices?customer=${id}&new=1`);

  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 text-sm mb-3 flex items-center gap-1" data-testid="back-btn">
        <ArrowLeft size={16} /> Back
      </button>
      <PageHeader
        title={customer.name}
        subtitle={customer.company || "Customer"}
        actions={
          <>
            <button className="btn-ghost flex items-center gap-2" onClick={() => setEditOpen(true)} data-testid="edit-customer-btn">
              <PencilSimple size={16} /> Edit
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={startInvoice} data-testid="new-invoice-from-customer">
              <Plus size={16} /> New Invoice
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="surface p-5">
          <div className="overline mb-3">Contact</div>
          <div className="space-y-2 text-sm">
            <Info label="Email" value={customer.email} mono />
            <Info label="Phone" value={customer.phone} mono />
            <Info label="Address" value={customer.address} />
          </div>
          {customer.notes && (
            <>
              <div className="overline mt-5 mb-2">Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{customer.notes}</p>
            </>
          )}
        </div>

        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="surface">
            <div className="px-5 py-4 border-b hairline flex justify-between">
              <h2 className="text-slate-900 font-semibold">Work Orders</h2>
              <span className="text-sm text-slate-400">{work_orders.length}</span>
            </div>
            {work_orders.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No work orders for this customer yet.</div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Number</th><th>Type</th><th>Status</th><th>Deadline</th></tr></thead>
                <tbody>
                  {work_orders.map((w) => (
                    <tr key={w.id} onClick={() => navigate(`/work-orders/${w.id}`)} data-testid={`cust-wo-${w.id}`}>
                      <td className="font-mono text-slate-900 font-medium">{w.number}</td>
                      <td><span className={`chip ${w.job_type}`}>{w.job_type}</span></td>
                      <td><span className={`chip ${normalizeStatus(w.status)}`}>{STATUS_LABELS[normalizeStatus(w.status)] || w.status}</span></td>
                      <td className="font-mono">{fmtDate(w.deadline)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="surface">
            <div className="px-5 py-4 border-b hairline flex justify-between">
              <h2 className="text-slate-900 font-semibold">Invoices</h2>
              <span className="text-sm text-slate-400">{invoices.length}</span>
            </div>
            {invoices.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No invoices yet.</div>
            ) : (
              <table className="data-table">
                <thead><tr><th>Number</th><th>Total</th><th>Paid</th><th>Status</th></tr></thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} onClick={() => navigate(`/invoices/${i.id}`)} data-testid={`cust-inv-${i.id}`}>
                      <td className="font-mono text-slate-900 font-medium">{i.number}</td>
                      <td className="font-mono">{money(i.total)}</td>
                      <td className="font-mono">{money(i.amount_paid)}</td>
                      <td><span className={`chip ${i.payment_status}`}>{i.payment_status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <CustomerTimeline
        workOrders={work_orders}
        invoices={invoices}
        onNavWO={(wid) => navigate(`/work-orders/${wid}`)}
        onNavInv={(iid) => navigate(`/invoices/${iid}`)}
      />

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            {["name","company","email","phone"].map((k) => (
              <div key={k}>
                <Label className="text-xs text-slate-600 capitalize">{k}</Label>
                <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Address</Label>
              <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-600">Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <button className="btn-ghost" onClick={() => setEditOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={save} data-testid="save-edit-customer">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value, mono }) => (
  <div>
    <div className="overline">{label}</div>
    <div className={`text-slate-800 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);

function CustomerTimeline({ workOrders, invoices, onNavWO, onNavInv }) {
  // Aggregate every event we know about into one chronological feed.
  const events = [];

  for (const inv of invoices || []) {
    events.push({
      at: inv.created_at,
      type: "invoice_created",
      icon: Receipt,
      title: `Invoice ${inv.number} created`,
      body: `Total ${Number(inv.total || 0).toFixed(2)} · Status ${inv.payment_status || "unpaid"}`,
      onClick: () => onNavInv(inv.id),
    });
    if (inv.payment_status === "paid" && (inv.amount_paid || 0) > 0) {
      events.push({
        at: inv.updated_at || inv.created_at,
        type: "invoice_paid",
        icon: Receipt,
        title: `Invoice ${inv.number} paid`,
        body: `Received ${Number(inv.amount_paid || 0).toFixed(2)}`,
        onClick: () => onNavInv(inv.id),
      });
    }
  }

  for (const w of workOrders || []) {
    events.push({
      at: w.created_at,
      type: "wo_created",
      icon: ClipboardText,
      title: `Work Order ${w.number} created`,
      body: `${(w.job_type || "").toUpperCase()}${w.deadline ? ` · deadline ${fmtDate(w.deadline)}` : ""}`,
      onClick: () => onNavWO(w.id),
    });
    if (w.signed_at) {
      events.push({
        at: w.signed_at,
        type: "wo_signed",
        icon: Signature,
        title: `${w.number} signed`,
        body: w.signed_by ? `by ${w.signed_by}` : "",
        onClick: () => onNavWO(w.id),
      });
    }
    for (const a of (w.activity_log || [])) {
      events.push({
        at: a.at,
        type: "activity",
        icon: ChatCircleText,
        title: `${w.number} note`,
        body: `${a.by ? a.by + ": " : ""}${a.text}`,
        onClick: () => onNavWO(w.id),
      });
    }
  }

  events.sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  return (
    <div className="mt-6 surface p-6" data-testid="customer-timeline">
      <div className="overline mb-4 flex items-center gap-2"><Package size={14} /> Activity Timeline</div>
      {events.length === 0 ? (
        <div className="text-sm text-slate-400 py-4">No activity yet — invoice + work-order events will appear here.</div>
      ) : (
        <ol className="relative border-l border-slate-200 ml-2">
          {events.map((e, i) => {
            const Icon = e.icon;
            return (
              <li key={i} className="mb-6 ml-6 last:mb-0 group cursor-pointer" onClick={e.onClick} data-testid={`timeline-event-${i}`}>
                <span className="absolute -left-3.5 flex items-center justify-center w-7 h-7 rounded-full bg-white border border-slate-200 group-hover:border-emerald-600 transition-colors">
                  <Icon size={14} className="text-emerald-700" />
                </span>
                <div className="text-xs font-mono text-slate-500 mb-0.5">{fmtDate(e.at)}</div>
                <div className="text-slate-900 font-medium group-hover:text-emerald-700 transition-colors">{e.title}</div>
                {e.body && <div className="text-sm text-slate-600 whitespace-pre-wrap">{e.body}</div>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
